/**
 * The table as the player has SEEN it so far.
 *
 * ============================== WHY THIS EXISTS ==================================
 * The engine is untouched by the overhaul, and `Game.startHand()` and `Game.act()` both
 * run every bot through to the next point the human is needed — synchronously, in one
 * call. By the time either returns, `game.tableState()` is already at the end of that
 * run: three opponents have acted, the flop is out, the pot has moved. The event stream
 * is the record of how it got there, in order, and the Director plays that record back
 * at a pace. So there have to be two tables: the ENGINE's, which is the truth and is
 * ahead, and the PRESENTED one, which is what the scene draws and which catches up one
 * event at a time.
 *
 * This file is the presented one. It is a reducer: a `TableState`, an event, a new
 * `TableState`. It is deliberately dumb — it computes nothing about poker that the
 * event does not carry. Stacks come from `stackAfter`, the pot from `potAfter`, the
 * board from `board`, the winners from `pot_push`. The one thing it derives is a seat's
 * `committed`, as the fall in its stack, because the engine's `committed` is what the
 * bet chip shows and the event has the two numbers that pin it down.
 *
 * It has to agree with the engine EXACTLY at every point where the two can be compared:
 * when the hero is to act, and when the hand is over. `director.test.ts` checks that on
 * a seeded session, hand by hand, and a drift between them is a bug here — the engine
 * is never wrong about its own table.
 * ===============================================================================
 */
import type { TableEvent } from "@engine/events";
import type { SeatView, TableState } from "@engine/table-state";
import type { Action } from "@engine/types";

/**
 * The one event the engine does not emit and the scene needs: whose turn it is.
 *
 * Plain JSON, driver-side, exactly as `AUDIT-ENGINE.md` §3 proposes. The engine's own
 * `toAct` is only observable from the outside AFTER a run, so the Director says it at
 * the moment the seat's clock starts.
 */
export interface ToActEvent { t: "to_act"; seat: number }

/**
 * Two more driver-side beats the engine folds into other events.
 *
 * `collect` is the street closing — every bet in front of a seat slides into the pot —
 * which the engine expresses as part of `board` (or of `pot_push` when nobody saw a
 * board). `refund` is an uncalled bet going back, which the engine expresses only as
 * the seat's `net` at `hand_end`. Each is split out so it can be its own animation with
 * its own duration, and each is plain JSON like everything else on the stream.
 */
export interface CollectEvent { t: "collect" }
export interface RefundEvent { t: "refund"; seat: number; amount: number }

export type SceneEvent = TableEvent | ToActEvent | CollectEvent | RefundEvent;

/** A deep copy; the reducer never writes into the state it was given. */
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/**
 * The table at the instant a hand begins, before the blinds go in.
 *
 * `engine` is the engine's snapshot of the hand — taken after it has already run, so
 * only its shape is used: seats, names, cosmetics, rules. `stacksAtStart` is where
 * every seat's chips were before this hand; the Director reads it off `game.stacks`,
 * which the engine only writes back at the end of a hand, or off the hand's own record
 * when the hand finished inside the same call.
 */
export function handStartState(
  engine: TableState, stacksAtStart: readonly number[], e: { hand: number; button: number; commitment: string },
): TableState {
  return {
    handNumber: e.hand,
    street: "preflop",
    board: [],
    pot: 0,
    currentBet: 0,
    toAct: null,
    button: e.button,
    commitment: e.commitment,
    rules: engine.rules,
    seats: engine.seats.map((s, i): SeatView => {
      const stack = stacksAtStart[i] ?? s.stack;
      return {
        ...s,
        stack,
        committed: 0,
        folded: false,
        allIn: false,
        sittingOut: stack <= 0,
        hole: null,
        equipped: s.equipped,
      };
    }),
  };
}

function maxCommitted(seats: readonly SeatView[]): number {
  let max = 0;
  for (const s of seats) if (!s.sittingOut && s.committed > max) max = s.committed;
  return max;
}

/**
 * The words for an action, for the context line and the band: "Hattie calls",
 * "You call". `self` picks the second person for the hero's own seat.
 */
export function actionLabel(a: Action, self = false): string {
  switch (a.kind) {
    case "fold": return self ? "fold" : "folds";
    case "check": return self ? "check" : "checks";
    case "call": return self ? "call" : "calls";
    case "bet": return self ? "bet" : "bets";
    case "raise": return self ? "raise" : "raises";
    case "allin": return self ? "are all in" : "is all in";
  }
}

/** Whether an action moved chips, for the timeline's choice of beat. */
export function movesChips(a: Action): boolean {
  return a.kind === "call" || a.kind === "bet" || a.kind === "raise" || a.kind === "allin";
}

/**
 * One event applied to the presented table.
 *
 * `hand_start` is not handled here — it needs the starting stacks, which are not in the
 * event — so the Director calls `handStartState` for it and this for everything else.
 */
export function applyEvent(
  prev: TableState, e: SceneEvent, stacksAtStart?: readonly number[],
): TableState {
  const s = clone(prev);
  switch (e.t) {
    case "hand_start":
      return s;

    case "to_act":
      s.toAct = e.seat;
      return s;

    case "collect":
      for (const seat of s.seats) seat.committed = 0;
      s.currentBet = 0;
      return s;

    case "refund": {
      const seat = s.seats[e.seat]!;
      seat.stack += e.amount;
      /*
       * PHASE 2B, BOX 0(a). Chips are back in front of this seat, so it is not all in
       * any more. Without this the plate kept reading ALL IN over a stack that had
       * money in it, and — because the ALL IN tag was where the number should be —
       * the flight landed on the word rather than on the figure. Same rule wherever
       * chips are handed to a seat: `refund`, `pot_push`, `hand_end`.
       */
      if (seat.stack > 0) seat.allIn = false;
      s.pot = Math.max(0, s.pot - e.amount);
      return s;
    }

    case "post": {
      const seat = s.seats[e.seat]!;
      seat.stack = e.stackAfter;
      seat.committed += e.amount;
      if (seat.stack <= 0) seat.allIn = true;
      s.pot += e.amount;
      s.currentBet = Math.max(s.rules.bigBlind, maxCommitted(s.seats));
      return s;
    }

    case "deal_hole":
    case "burn":
      return s;

    case "hole_shown":
    case "showdown": {
      s.seats[e.seat]!.hole = [...e.cards];
      return s;
    }

    case "action": {
      const seat = s.seats[e.seat]!;
      /*
       * `to` is the seat's total on the street after acting and is what the engine
       * committed, to the chip; what the seat put in is the rise in `to` over what it
       * had already committed. The stack difference is the fallback, and only that:
       * summing differences in floating point drifts by a billionth per action, and the
       * comparison with the engine is exact.
       *
       * THE SPOILER (Phase 3b, defect 1). When an action ENDS the hand — the call that
       * closes an all-in, the last check on the river — the engine settles inside
       * `Hand.apply()` before the event is written, so `stackAfter` on that one event
       * already holds the winnings and the refunds. Shown as-is, the hero read $4,850
       * the instant the call landed, three streets before the pot moved. So the stack
       * after an action is what the seat had less what it put in, never more than
       * that: winnings arrive with `pot_push`, refunds with `hand_end`, each in its own
       * time. Where the engine's number is the smaller one it is the truth (a short
       * all-in call) and it wins.
       */
      const putByTo = e.action.to !== undefined ? Math.max(0, e.action.to - seat.committed) : 0;
      const expected = Math.max(0, seat.stack - putByTo);
      const stackAfter = Math.min(e.stackAfter, expected);
      const put = seat.stack - stackAfter;
      seat.stack = stackAfter;
      if (e.action.to !== undefined && put > 0) seat.committed = e.action.to;
      else if (put > 0) seat.committed += put;
      if (e.action.kind === "fold") seat.folded = true;
      if (seat.stack <= 0 && !seat.folded) seat.allIn = true;
      s.pot = e.potAfter;
      s.currentBet = Math.max(s.currentBet, maxCommitted(s.seats));
      /* Whoever acts next is announced by `to_act`; until then nobody is. */
      s.toAct = null;
      return s;
    }

    case "board": {
      /*
       * A street closed. The chips in front of every seat are now in the pot — which
       * `pot` already counted, because the engine's pot is everything contributed — so
       * only `committed` moves, to zero. A run-out carries several streets in one event
       * and lands them all; the timeline is what spreads them out.
       */
      for (const seat of s.seats) seat.committed = 0;
      s.currentBet = 0;
      s.street = e.street;
      s.board.push(...e.cards);
      return s;
    }

    case "pot_push": {
      for (const seat of s.seats) seat.committed = 0;
      s.currentBet = 0;
      const winner = s.seats[e.seat]!;
      winner.stack += e.amount;
      /* Box 0(a): the pot has landed, so the winner is no longer all in. */
      if (winner.stack > 0) winner.allIn = false;
      s.pot = Math.max(0, s.pot - e.amount);
      s.toAct = null;
      return s;
    }

    case "button_move":
      s.button = e.to;
      return s;

    case "hand_end": {
      /*
       * `net` settles anything the pushes did not say out loud.
       *
       * An uncalled bet is REFUNDED, not won: the engine's pots exclude it and no
       * `pot_push` carries it, so after the last push the presented pot still holds the
       * refund and the bettor's stack is short by it. `net` is per seat across the whole
       * hand, so start plus net is exactly where every stack ends — and the timeline
       * reads the same difference to fly the refund back before this lands.
       */
      if (stacksAtStart) {
        for (let i = 0; i < s.seats.length; i++) {
          const start = stacksAtStart[i];
          if (start !== undefined) s.seats[i]!.stack = start + (e.net[i] ?? 0);
        }
      }
      /*
       * Box 0(a) again, for the seats no push named: an uncalled bet settled here, or
       * a side pot that came back. A seat that ends the hand with chips is not all in.
       * One that busted keeps the tag, which is the truth about it.
       */
      for (const seat of s.seats) if (seat.stack > 0) seat.allIn = false;
      for (const seat of s.seats) seat.committed = 0;
      s.currentBet = 0;
      s.pot = 0;
      s.toAct = null;
      return s;
    }
  }
}

/** The chips still owed back to seats when the hand ends: refunds and rounding. */
export function refundsAt(
  state: TableState, stacksAtStart: readonly number[], net: readonly number[],
): { seat: number; amount: number }[] {
  const out: { seat: number; amount: number }[] = [];
  for (let i = 0; i < state.seats.length; i++) {
    const start = stacksAtStart[i];
    if (start === undefined) continue;
    const owed = start + (net[i] ?? 0) - state.seats[i]!.stack;
    if (owed > 1e-9) out.push({ seat: i, amount: owed });
  }
  return out;
}
